import { getReferenceString, resolveId } from '@medplum/core';
import { CareTeam, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UseCareTeamResult {
  careTeam: CareTeam | undefined;
  careTeamLoading: boolean;
  careTeamProviders: Practitioner[];
  careTeamProvidersLoading: boolean;
  refreshCareTeam: () => Promise<void>;
}

export function useCareTeam(patient: Patient | undefined): UseCareTeamResult {
  const medplum = useMedplum();
  const [careTeam, setCareTeam] = useState<CareTeam | undefined>(undefined);
  const [careTeamLoading, setCareTeamLoading] = useState(false);
  const [careTeamProviders, setCareTeamProviders] = useState<Practitioner[]>([]);
  const [careTeamProvidersLoading, setCareTeamProvidersLoading] = useState(false);

  const loadCareTeamProviders = useCallback(async () => {
    if (!careTeam) {
      setCareTeamProviders([]);
      return;
    }

    setCareTeamProvidersLoading(true);

    try {
      const practitionerIds = careTeam.participant?.map((p) => resolveId(p.member)).filter(Boolean) as
        | string[]
        | undefined;
      if (practitionerIds && practitionerIds.length > 0) {
        const practitioners = await medplum.searchResources('Practitioner', {
          _id: practitionerIds.join(','),
        });
        setCareTeamProviders(practitioners);
      } else {
        setCareTeamProviders([]);
      }
    } catch {
      setCareTeamProviders([]);
    } finally {
      setCareTeamProvidersLoading(false);
    }
  }, [medplum, careTeam]);

  const loadCareTeamData = useCallback(async () => {
    if (!patient) {
      setCareTeam(undefined);
      setCareTeamProviders([]);
      return;
    }

    // Clear providers immediately to prevent showing old patient's care team
    setCareTeamProviders([]);
    setCareTeamLoading(true);

    const result = await medplum.searchOne('CareTeam', {
      subject: getReferenceString(patient),
    });
    setCareTeam(result);
    setCareTeamLoading(false);
  }, [patient, medplum]);

  useEffect(() => {
    loadCareTeamData();
  }, [loadCareTeamData]);

  useEffect(() => {
    loadCareTeamProviders();
  }, [loadCareTeamProviders]);

  return {
    careTeam,
    careTeamLoading,
    careTeamProviders,
    careTeamProvidersLoading,
    refreshCareTeam: loadCareTeamData,
  };
}
